import React, { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { AGGREGATION_FIELDS, ASSET_TYPES, WHITE_COLOR } from '@/shared/api/openmetadata';
import type { OpenMetadataAggregations } from '@/shared/api/openmetadata';
import { sanitizeDescription } from '@/shared/lib/sanitize';
import { highlightText } from '@/shared/lib/text';
import { FilterDropdown } from '@/shared/ui/FilterDropdown';
import type { FilterDropdownOption } from '@/shared/ui/FilterDropdown';
import { Icon } from '@/shared/ui/Icon';

import { useFilterStore } from '@/entities/discovery';
import { useSearchStore } from '@/entities/search/domain/store';

import type { CertificationOption } from '@/features/asset-detail/api/certificationsApi';
import { fetchAvailableCertifications } from '@/features/asset-detail/api/certificationsApi';
import { searchIntegrationWithPagination } from '@/features/dataSearch/api/integrationSearchApi';

import styles from './CertificationDropdown.module.css';

const CERTIFICATION_SORT_ORDER: Record<string, number> = {
  'certification.gold': 1,
  'certification.silver': 2,
  'certification.bronze': 3,
} as const;

const GOLD_CERTIFICATION_DISPLAY_NAME = 'Gold';
const GOLD_CERTIFICATION_FQN_LOWER = 'certification.gold';
const GOLD_CERTIFICATION_KEY_FRAGMENT = 'gold';

const isGoldCertification = (cert: CertificationOption, certKeyLower: string): boolean => {
  if (cert.name === GOLD_CERTIFICATION_DISPLAY_NAME) return true;
  if (certKeyLower === GOLD_CERTIFICATION_FQN_LOWER) return true;
  return certKeyLower.includes(GOLD_CERTIFICATION_KEY_FRAGMENT);
};

/**
 * Aggregation 데이터로부터 성숙도 필터 옵션 생성
 *
 * @description
 * OpenMetadata aggregation과 실험 스탠드얼론 카운트를 결합하여
 * Gold, Silver, Bronze 성숙도 옵션을 생성합니다.
 * Gold의 경우 실험 스탠드얼론 feature_set/dataset 카운트가 포함됩니다.
 *
 * @param initialAggregations - OpenMetadata 초기 aggregation 데이터
 * @param availableCertifications - 사용 가능한 성숙도 목록
 * @param experimentStandaloneCount - 실험 스탠드얼론 자산 카운트
 * @returns 정렬된 성숙도 필터 옵션 배열
 */
const generateCertificationOptionsFromAggregations = (
  initialAggregations: OpenMetadataAggregations | null,
  availableCertifications: CertificationOption[],
  experimentStandaloneCount: number
): FilterDropdownOption[] => {
  const certificationBuckets = initialAggregations?.[AGGREGATION_FIELDS.CERTIFICATION]?.buckets || [];

  const certificationCountMap = new Map<string, number>();
  certificationBuckets.forEach((bucket) => {
    const normalizedKey = bucket.key.toLowerCase();
    certificationCountMap.set(normalizedKey, bucket.doc_count);
  });

  const options: FilterDropdownOption[] = availableCertifications.map((cert) => {
    const certKey = cert.fullyQualifiedName.toLowerCase();
    const omCount = certificationCountMap.get(certKey) || 0;
    const count = isGoldCertification(cert, certKey) ? omCount + experimentStandaloneCount : omCount;

    return {
      key: certKey,
      label: cert.name,
      value: certKey,
      disabled: false,
      data: {
        sortOrder: CERTIFICATION_SORT_ORDER[certKey] || 999,
        count,
        color: cert.color,
        description: cert.description,
      },
    };
  });

  return [...options].sort((a, b) => {
    if (a.disabled && !b.disabled) return 1;
    if (!a.disabled && b.disabled) return -1;

    const aOrder = typeof a.data?.sortOrder === 'number' ? a.data.sortOrder : 999;
    const bOrder = typeof b.data?.sortOrder === 'number' ? b.data.sortOrder : 999;
    return aOrder - bOrder;
  });
};

type CertificationDropdownProps = {
  width?: number | string;
};

export const CertificationDropdown: React.FC<CertificationDropdownProps> = ({ width }) => {
  const { filters, updateCertificationFilter } = useFilterStore();
  const { initialAggregations } = useSearchStore();

  const { data: availableCertifications = [], isLoading: isLoadingCertifications } = useQuery({
    queryKey: ['availableCertifications'],
    queryFn: fetchAvailableCertifications,
    staleTime: 5 * 60 * 1000,
  });

  const { data: experimentStandaloneCount = 0 } = useQuery({
    queryKey: ['searchIntegration', 'experimentStandaloneGoldProxyCount'],
    queryFn: async () => {
      const res = await searchIntegrationWithPagination({
        size: 1,
        cursor: null,
        sortBy: 'created_at',
        sortOrder: 'desc',
        assetTypes: [ASSET_TYPES.FEATURE_SET, ASSET_TYPES.DATASET],
      });
      return res.totalCount;
    },
    staleTime: 60 * 1000,
  });

  const selectedCertifications = useMemo(() => filters.certification || [], [filters.certification]);

  const certificationOptions = useMemo(() => {
    const options = generateCertificationOptionsFromAggregations(
      initialAggregations,
      availableCertifications,
      experimentStandaloneCount
    );

    return options.map((option) => ({
      ...option,
      data: {
        ...option.data,
        sanitizedDescription:
          typeof option.data?.description === 'string' ? sanitizeDescription(option.data.description) : undefined,
      },
    }));
  }, [initialAggregations, availableCertifications, experimentStandaloneCount]);

  return (
    <FilterDropdown
      buttonText="데이터 성숙도"
      searchPlaceholder="데이터 성숙도 검색..."
      selectedValues={selectedCertifications}
      options={certificationOptions}
      onSelectionChange={updateCertificationFilter}
      width={width}
      disabled={isLoadingCertifications}
      renderItem={(option, _isSelected, _onToggle, searchQuery) => {
        const color = typeof option.data?.color === 'string' ? option.data.color : undefined;
        const sanitizedDescription =
          typeof option.data?.sanitizedDescription === 'string' ? option.data.sanitizedDescription : undefined;

        return (
          <div className={styles.certificationHeader}>
            <div className={styles.certificationContent}>
              {color && (
                <div className={styles.certificationIcon} style={{ backgroundColor: color }}>
                  <Icon name="gem" size={24} style={{ color: WHITE_COLOR }} />
                </div>
              )}
              <div className={styles.certificationInfo}>
                <div className={styles.certificationName}>
                  {highlightText(option.label, searchQuery, { className: 'highlight' })}
                </div>
                {sanitizedDescription && (
                  <div
                    className={styles.certificationDescription}
                    dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};
